import App from '@app/app';
import { Injectable, inject } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import type { AddOn } from '@app/interface/store/add-on.interface';
import type { LookDto } from '@app/interface/response/master-event.dto';
import type { ISubject } from '@app/interface/cart/subject.interface';
import type { OrderLineDetail } from '@app/interface/cart/order-line.interface';
import type { TranslateProviderService } from '@app/interface/service/translate-provider-service.interface';
import { DigitalDownloadType } from '@app/interface/digital/digital-download.interface';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';

@Injectable({ providedIn: 'root' })
export class DigitalDownloadService {
  private translateProviderService: TranslateProviderService = inject(angularjs.TranslateProviderService);

  getDigitalDownloadType(item?: AddOn): DigitalDownloadType | null {
    if (item?.digitalDelivery) {
      return DigitalDownloadType.SOCIAL;
    }
    if (item?.canBeDigitalEligible) {
      return DigitalDownloadType.SFLY;
    }
    return null;
  }

  getFirstDigitalDownloads(orderLineDetails: OrderLineDetail[]): Map<OrderLineDetail, DigitalDownloadType> {
    const firstOccurrences = new Map<OrderLineDetail, DigitalDownloadType>();

    if (this.translateProviderService.isCanadianDomain()) {
      return firstOccurrences;
    }

    const seenLooks = new Set<string>();

    orderLineDetails.forEach((orderLineDetail, index) => {
      const { item, look } = orderLineDetail;

      const digitalDownloadType = this.getDigitalDownloadType(item);

      if (!digitalDownloadType) return false;

      const key = this.buildKey(index, item, look, digitalDownloadType);

      if (!seenLooks.has(key)) {
        seenLooks.add(key);
        firstOccurrences.set(orderLineDetail, digitalDownloadType);
      }
    });

    return firstOccurrences;
  }

  getSubjectFirstDigitalDownloads(subject: ISubject) {
    return this.getFirstDigitalDownloads(subject.getOrderLineDetails());
  }

  private buildKey(
    index: number,
    item: AddOn,
    look: LookDto | undefined,
    digitalDownloadType: DigitalDownloadType
  ): string {
    const layoutThemeId = item.lookSelectionAllowed ? String(look!.layoutThemeID) : String(item.defaultLayoutThemeId);

    const key = `${layoutThemeId}|${digitalDownloadType}`;

    if (digitalDownloadType === DigitalDownloadType.SOCIAL) {
      return `${key}|${index}`;
    }

    return key;
  }
}

App.factory('DigitalDownloadService', downgradeInjectable(DigitalDownloadService));
